// The Pulse message catalog (Slice 6, Flow 2; docs/phase-0/slice6-ux-flows.md §4;
// docs/phase-0/slice6-component-designs.md §4). Mirrors apps/web/src/copy/build.ts's own
// discipline (its header, build.ts:1-37) byte-for-byte: every string the ux spec tags
// `[copy: flagged for review]` is reproduced VERBATIM below and rendered as DRAFT, never
// finalized by this build lane (CLAUDE.md §8); every other key is a plain structural
// label (a heading, a button name, a metric's own display name) - not a new compliance
// or business claim - so it carries no separate review flag, per build.ts's/certify.ts's
// own bare-label convention.
//
// SCOPED, ENGLISH-ONLY, LOCAL TO apps/web - the same gap build.ts/certify.ts's own
// headers name: no i18n-routed ICU MessageFormat mechanism exists in this repo yet
// (the i18n-engineer's lane, `i18n-localization`). `pulseCopy()` below does plain
// `{token}` substitution only - no ICU plural rules.
//
// NEVER INVENTED HERE (CLAUDE.md §8, D27/D48): no cost, token, per-fix, or per-action
// figure or word appears anywhere in this file - the seven pulse metrics are the
// founder's OWN product signals (packages/shared/src/model/pulse.ts:13-21), never a
// Nightcrew-cost surface. `pulse.metric.trend` states the comparison plainly against the
// actual PRIOR captured date rather than asserting a specific cadence ("vs last week")
// the landed wire contract does not state (see MetricTrend.tsx's own header) - a
// deliberate correction of the ux spec's own illustrative "up 12% vs. last week" example
// (slice6-ux-flows.md:675-676) so this file never asserts an unverified interval.
export const PULSE_COPY = {
  // ---- The AppDetail "Pulse" tab (ux spec §4, component-designs.md §4) ----
  "pulse.tab.title": "Pulse",
  "pulse.metrics.heading": "Your seven metrics",
  // [copy: flagged for review] - ux spec §4.4 empty-state shell, verbatim.
  "pulse.metrics.emptyIntro": "No signals yet for {appName}.",
  "pulse.loading.status": "Loading your pulse...",
  "pulse.loaded.status": "Pulse loaded.",
  "pulse.errors.body": "We couldn't load your pulse. Try again.",
  "pulse.errors.retryCta": "Try again",

  // ---- The seven metric cards' own names (component-designs.md §4.1, literal) ----
  "pulse.metric.label.active_users": "Active users",
  "pulse.metric.label.signups": "Signups",
  "pulse.metric.label.retention": "Retention",
  "pulse.metric.label.revenue": "Revenue",
  "pulse.metric.label.errors": "Errors",
  "pulse.metric.label.p95": "p95 (latency)",
  "pulse.metric.label.uptime": "Uptime",
  "pulse.metric.asOf": "as of {date}",
  // [copy: flagged for review] - ux spec §4.4, the idea-route/validated-stage empty copy.
  "pulse.metric.empty.validated": "Install the standard SDK to start seeing this.",
  // [copy: flagged for review] - ux spec §4.4, the shipped-with-no-traffic empty copy.
  "pulse.metric.empty.shipped": "Waiting on your first signals.",
  // Corrected from the ux spec's own illustrative "vs. last week" (see this file's
  // header) - states the actual prior date instead of an unverified cadence.
  "pulse.metric.trend": "{delta} vs {priorDate}",

  // ---- Churn signals (ux spec §4.3, component-designs.md §4.2) ----
  "pulse.churnSignals.heading": "Churn signals",
  // [copy: flagged for review] - ux spec §4.4, verbatim.
  "pulse.churnSignals.empty": "Nothing flagged this period.",
  "pulse.churnSignals.routedChip": "Routed to your crew",
} as const;

export type PulseCopyKey = keyof typeof PULSE_COPY;

/**
 * Plain `{token}` substitution only - no ICU plural rules (see the header note and
 * `copy/build.ts`'s identical implementation, which this mirrors byte-for-byte). A
 * missing var leaves the literal `{token}` in place rather than throwing.
 */
export const pulseCopy = (key: PulseCopyKey, vars?: Record<string, string | number>): string => {
  const template = PULSE_COPY[key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match: string, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
};
